/**
 * WhatsApp click-to-chat link helpers.
 *
 * Tenants store their WhatsApp contact in `social_links.whatsapp` in whatever
 * shape they typed: a full `https://wa.me/9665…` link, an
 * `api.whatsapp.com/send?phone=…` link, or a bare phone number
 * (`+966 55 123 4567`). All shapes normalise to E.164 digits, from which a
 * canonical `https://wa.me/<digits>?text=<prefill>` URL is built.
 */

/**
 * Extract the phone digits from any of the accepted WhatsApp value shapes.
 * Returns null when no plausible number is present.
 */
export function extractWhatsAppNumber(raw: string | null | undefined): string | null {
  if (!raw) return null
  const value = raw.trim()
  if (!value) return null

  let candidate = value
  if (/^(https?:)?\/\//i.test(value) || /^(wa\.me|api\.whatsapp\.com|whatsapp\.com)\//i.test(value)) {
    try {
      const url = new URL(value.startsWith('http') ? value : `https://${value}`)
      candidate = url.searchParams.get('phone') || url.pathname
    } catch {
      candidate = value
    }
  }

  const digits = candidate.replace(/\D/g, '')
  // 8 digits = shortest national significant numbers; 15 = E.164 max.
  if (digits.length < 8 || digits.length > 15) return null
  return digits
}

/**
 * Build a `wa.me` click-to-chat URL with an optional prefilled message.
 * Returns null when the stored value contains no usable number (including the
 * admin-side placeholder `https://wa.me/966XXXXXXXXX`).
 */
export function buildWhatsAppUrl(raw: string | null | undefined, text?: string): string | null {
  const number = extractWhatsAppNumber(raw)
  if (!number) return null
  const query = text ? `?text=${encodeURIComponent(text)}` : ''
  return `https://wa.me/${number}${query}`
}
